import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { DRAWER_WIDTH } from 'constants/ui';
import { USER_CREATED_ID, USER_EDITED_ID } from 'constants/userActivities';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import { AppDispatch, RootState } from 'store/store';
import { deleteRequest, getRequest, patchRequest, postRequest } from 'api/apiClient';
import { capitalizeName, formatFullName, hasPermission, transformNullToEmptyString } from 'utils/utils';
import { User } from 'types/userTypes';
import { Role } from 'types/roleTypes';
import UserForm from './tabs/UserForm';
import OptionsForm from './tabs/OptionsForm';
import { useTranslation } from 'react-i18next';
import { describeUser } from 'store/slices/usersSlice';
import { fetchLocations, describeLocation, describeLocationWorkflow } from 'store/slices/locationsSlice';
import { Location } from 'types/locationTypes';
import i18n from 'i18n/i18n';

interface UsersDrawerProps {
    open: boolean;
    onClose: () => void;
    selectedUser: User | null;
    setUsers: (users: User[]) => void;
    roles: Role[];
}

const UsersDrawer: React.FC<UsersDrawerProps> = ({ open, onClose, selectedUser, setUsers, roles }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const locations = useSelector((state: RootState) => state.locations.locations);
    const globalLocation = useSelector((state: RootState) => state.locations.globalLocation);

    const hasUserWrite = hasPermission(tokenScopeList, orgId, 'o', 'user:w');

    const [activeKey, setActiveKey] = useState<string>('user');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedUserRoles, setSelectedUserRoles] = useState<Role[]>([]);
    const [locationAccess, setLocationAccess] = useState<number[]>([]);

    const [isFetchingSelectedUserRoles, setIsFetchingSelectedUserRoles] = useState<boolean>(false);
    const [isFetchingLocationAccess, setIsFetchingLocationAccess] = useState<boolean>(false);
    const [isFetchingSelectedUserSites, setIsFetchingSelectedUserSites] = useState<boolean>(false);
    const [isFetchingSuperAdminUsers, setIsFetchingSuperAdminUsers] = useState<boolean>(false);
    const [superAdminUsers, setSuperAdminUsers] = useState<User[]>([]);
    // Drawer-local default location for the user being edited.
    // Kept separate from redux `state.users.selectedUserSites` (which is the
    // logged-in user's default site) to avoid cross-contamination.
    const [defaultLocation, setDefaultLocation] = useState<Location | null>(null);

    const [form] = OpForm.useForm();
    const { t } = useTranslation();
    // Reset the active tab to 'user' when the drawer opens
    useEffect(() => {
        if (open) {
            setActiveKey('user');
        } else {
            // Reset states when drawer closes
            setLocationAccess([]);
            setSelectedUserRoles([]);
            setSuperAdminUsers([]);
            setDefaultLocation(null);
            setIsFetchingSelectedUserRoles(false);
            setIsFetchingLocationAccess(false);
            setIsFetchingSelectedUserSites(false);
            setIsFetchingSuperAdminUsers(false);
        }
    }, [open]);

    const initialValues = selectedUser ? {
        ...selectedUser,
        identity: {
            ...selectedUser.identity,
            firstName: capitalizeName(selectedUser.identity?.firstName),
            lastName: capitalizeName(selectedUser.identity?.lastName),
            middleName: capitalizeName(selectedUser.identity?.middleName),
        },
        startDate: selectedUser.startDate && dayjs(selectedUser.startDate).isValid()
            ? dayjs.utc(selectedUser.startDate).local()
            : undefined,
        endDate: selectedUser.endDate && dayjs(selectedUser.endDate).isValid()
            ? dayjs.utc(selectedUser.endDate).local()
            : undefined,
        roleId: selectedUserRoles.map(role => role.id),
        defaultLocationId: defaultLocation?.id,
        limitLocations: locationAccess.length > 0,
        locationIds: locationAccess,
    } : {
        limitLocations: false,
        locationIds: [],
    };

    // Only super admins (users holding the admin role) may edit location limitation.
    const isCurrentUserSuperAdmin = superAdminUsers.some(user => user.id === globalUserId);

    const tabItems = [
        {
            key: 'user',
            label: t('user.tabs.user'),
            children: <UserForm selectedUser={selectedUser} locations={locations} roles={roles} locationAccess={locationAccess} isReadOnly={!hasUserWrite} isSuperAdmin={isCurrentUserSuperAdmin} />,
        },
        {
            key: 'options',
            label: t('user.tabs.options'),
            children: <OptionsForm />,
        },
    ];

    // Fetch selected user roles and location access
    useEffect(() => {
        if (selectedUser) {
            const fetchSelectedUserRoles = async () => {
                setIsFetchingSelectedUserRoles(true);
                try {
                    const selectedUserRoles = await getRequest(`/orgs/${orgId}/users/${selectedUser.id}/roles`);
                    setSelectedUserRoles(selectedUserRoles.data);
                } catch (error) {
                    console.log("Failed to fetch selected user roles.");
                } finally {
                    setIsFetchingSelectedUserRoles(false);
                }
            };
            fetchSelectedUserRoles();

            // fetch selected user location access
            const fetchLocationAccess = async () => {
                setIsFetchingLocationAccess(true);
                try {
                    const response = await getRequest(`/orgs/${orgId}/users/${selectedUser.id}/locationAccess`);
                    const siteIds = response.data.map((access: any) => access.siteId);
                    setLocationAccess(siteIds);
                } catch (error) {
                    console.log("Failed to fetch location access.");
                    setLocationAccess([]);
                } finally {
                    setIsFetchingLocationAccess(false);
                }
            };
            fetchLocationAccess();

            // fetch selected user's default location (drawer-local; does not touch redux)
            const fetchUserDefaultLocation = async () => {
                setIsFetchingSelectedUserSites(true);
                try {
                    const response = await getRequest(`/orgs/${orgId}/users/${selectedUser.id}/sites`);
                    setDefaultLocation(response.data?.[0] ?? null);
                } catch (error) {
                    console.log("Failed to fetch selected user default location.");
                    setDefaultLocation(null);
                } finally {
                    setIsFetchingSelectedUserSites(false);
                }
            };
            fetchUserDefaultLocation();
        } else {
            // No selected user (creating a new user): reset selected-user states
            setIsFetchingSelectedUserRoles(false);
            setIsFetchingLocationAccess(false);
            setIsFetchingSelectedUserSites(false);
        }

        // Fetch super admin users regardless of selectedUser so that we can
        // determine whether the logged-in user is a super admin. This is needed
        // even when creating a new user, to enable the location limitation
        // checkbox for super admins.
        const superAdminRole = roles.find(role => role.admin);
        if (superAdminRole) {
            const fetchSuperAdminUsersData = async () => {
                setIsFetchingSuperAdminUsers(true);
                try {
                    const superAdminUsers = await getRequest(`/orgs/${orgId}/roles/${superAdminRole.id}/users`);
                    const filteredSuperAdminUsers = superAdminUsers.data.filter((user: User) => user.status === 1);
                    setSuperAdminUsers(filteredSuperAdminUsers);
                } catch (error) {
                    console.log("Failed to fetch super admin users.");
                } finally {
                    setIsFetchingSuperAdminUsers(false);
                }
            };
            fetchSuperAdminUsersData();
        } else {
            // No super admin role, so no loading needed
            setIsFetchingSuperAdminUsers(false);
        }
    }, [dispatch, orgId, selectedUser, roles]);

    const handleSubmit = useCallback(async ({ values, touchedValues }: IOnSubmitArgs) => {
        // Validate all fields first
        try {
            await form.validateFields();
        } catch (errorInfo: any) {
            // If validation fails, switch to user tab and scroll to error
            if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
                setActiveKey('user');
                setTimeout(() => {
                    const transferElement = document.querySelector('.ant-transfer');
                    if (transferElement) {
                        transferElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            }
            return;
        }

        setIsSubmitting(true);

        try {
            if (touchedValues && Object.keys(touchedValues).length > 0) {
                const transformedValues = transformNullToEmptyString(touchedValues);
                delete transformedValues.defaultLocationId;
                delete transformedValues.limitLocations;
                delete transformedValues.locationIds;

                // Rename identity.mobilePhone to identity.mobile if it exists
                if (transformedValues.identity?.mobilePhone) {
                    transformedValues.identity.mobile = transformedValues.identity.mobilePhone;
                    delete transformedValues.identity.mobilePhone; // Remove the original key
                }

                if (transformedValues.startDate) {
                    transformedValues.startDate = dayjs(values.startDate).startOf('day').utc().format();
                }

                if (transformedValues.endDate) {
                    transformedValues.endDate = dayjs(values.endDate).endOf('day').utc().format();
                }

                // Validation for date order
                if (values.startDate && values.endDate
                    && dayjs(values.endDate).isBefore(dayjs(values.startDate))) {
                    notification.error({
                        message: t('notification.error'),
                        description: t('user.notification.deleteErrorDateOrder'),
                        placement: 'bottomRight',
                    });
                    return;
                }

                // Check if "Super Admin" role is being removed
                const superAdminRole = roles.find(role => role.admin);
                if (superAdminRole && transformedValues.roleId) {
                    const currentUserRoleIDs = selectedUserRoles.map(role => role.id);
                    const isRemovingSuperAdmin = currentUserRoleIDs.includes(superAdminRole.id) && !transformedValues.roleId.includes(superAdminRole.id);

                    if (isRemovingSuperAdmin) {
                        // Check if the only remaining "Super Admin" user is being deleted
                        if (superAdminUsers.length === 1 && superAdminUsers[0].id === selectedUser?.id) {
                            notification.error({
                                message: t('notification.error'),
                                description: t('user.notification.deleteErrorSuperAdmin', { role: superAdminRole.name }),
                                placement: 'bottomRight',
                            });
                            return;
                        }
                    }
                }

                if (selectedUser) { // existing user
                    await patchRequest(`/orgs/${orgId}/users/${selectedUser.id}`, transformedValues);

                    // default location exists && changed -> delete default location
                    if (defaultLocation && defaultLocation.id !== values.defaultLocationId) {
                        await deleteRequest(`/orgs/${orgId}/sites/${defaultLocation.id}/users/${selectedUser.id}`);
                    }

                    // new default location exists && changed -> create default location
                    if (values.defaultLocationId && defaultLocation?.id !== values.defaultLocationId) {
                        await postRequest(`/orgs/${orgId}/sites/${values.defaultLocationId}/users/${selectedUser.id}`, {});
                    }

                    // Update location access last to avoid permission denied errors
                    // Check if limitLocations or locationIds changed
                    const shouldUpdateLocationAccess =
                        touchedValues.locationIds !== undefined ||
                        touchedValues.limitLocations !== undefined;

                    if (shouldUpdateLocationAccess) {
                        const newLocationIds = values.limitLocations ? (values.locationIds || []) : [];
                        await patchRequest(`/orgs/${orgId}/users/${selectedUser.id}/locationAccess`, {
                            siteIds: newLocationIds
                        });

                        // If the logged-in user updated their own location access, refresh locations
                        if (selectedUser.id === globalUserId) {
                            const fetchedLocationsResult = await dispatch(fetchLocations({ orgId, status: 1 })).unwrap();
                            const newLocations = fetchedLocationsResult.data;

                            // Check if current globalLocation is still in the new locations list
                            const isGlobalLocationStillAvailable = newLocations.some(
                                (loc) => loc.id === globalLocation?.id
                            );

                            // If not available, set the first available location as globalLocation
                            if (!isGlobalLocationStillAvailable && newLocations.length > 0) {
                                const firstLocation = newLocations[0];
                                await dispatch(describeLocation({
                                    orgId,
                                    locationId: firstLocation.id,
                                    global: true
                                }));
                                await dispatch(describeLocationWorkflow({
                                    orgId,
                                    locationId: firstLocation.id,
                                    global: true
                                }));
                            }
                        }
                    }

                    await postRequest(`/orgs/${orgId}/userActivity`, {
                        userId: globalUserId,
                        activityId: USER_EDITED_ID,
                        details: formatFullName(values.identity.firstName, values.identity.middleName, values.identity.lastName)
                    });
                    if (selectedUser.id === globalUserId) {
                        await dispatch(describeUser({ orgId, userId: globalUserId, global: true }));
                    }

                    notification.success({
                        message: t('notification.success'),
                        description: t('user.notification.updateSuccess'),
                        placement: 'bottomRight',
                    });
                } else { // new user
                    try {
                        const response = await postRequest(`/orgs/${orgId}/users`, transformedValues);
                        const newUserId = response.data[0].id;

                        // Set location access for new user
                        const newLocationIds = values.limitLocations ? (values.locationIds || []) : [];
                        if (newLocationIds.length > 0) {
                            await patchRequest(`/orgs/${orgId}/users/${newUserId}/locationAccess`, {
                                siteIds: newLocationIds
                            });
                        }

                        if (values.defaultLocationId) {
                            await postRequest(`/orgs/${orgId}/sites/${values.defaultLocationId}/users/${newUserId}`, {})
                        }

                        await postRequest(`/auth/resetPassword?language=${i18n.language.split('-')[0]}`, {
                            email: response.data[0].identity.email,
                        });

                        await postRequest(`/orgs/${orgId}/userActivity`, {
                            userId: globalUserId,
                            activityId: USER_CREATED_ID,
                            details: formatFullName(values.identity.firstName, values.identity.middleName, values.identity.lastName)
                        });
                        notification.success({
                            message: t('notification.success'),
                            description: t('user.notification.createSuccess'),
                            placement: 'bottomRight',
                        });
                    } catch (error) {
                        notification.error({
                            message: t('notification.error'),
                            description: t('user.notification.createErrorExists'),
                            placement: 'bottomRight',
                        });
                    }
                }
                // Re-fetch the updated list of users
                const updatedUsers = await getRequest(`/orgs/${orgId}/users`);
                const filteredUpdatedUsers = updatedUsers.data.filter((user: User) => user.status === 1);
                setUsers(filteredUpdatedUsers);
            }
            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('user.notification.saveError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [dispatch, orgId, form, onClose, t, globalUserId, roles, selectedUser, selectedUserRoles, defaultLocation, setUsers, superAdminUsers, globalLocation?.id]);

    return (
        <OpFormDrawer
            form={form}
            title={selectedUser ? formatFullName(selectedUser.identity.firstName, selectedUser.identity.middleName, selectedUser.identity.lastName) : t('user.addTitle')}
            width={DRAWER_WIDTH}
            open={open}
            onClose={onClose}
            isFormLoading={isFetchingSelectedUserRoles || isFetchingLocationAccess || isFetchingSelectedUserSites || isFetchingSuperAdminUsers || isSubmitting}
            isFormReadOnly={!hasUserWrite}
            formComponent={
                <OpForm
                    form={form}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    hasError={false}
                    defaultButtons={false}
                    isReadOnly={!hasUserWrite}
                >
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                </OpForm>
            }
        />
    );
};

export default UsersDrawer;
